import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ChangeFieldType,
  ChangeFieldTypeSchema,
  type FieldDefinition,
  type FieldType,
} from '@servicetim/shared';
import { useChangeFieldType } from '@servicetim/ui/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@servicetim/ui/components';
import { TriangleAlert } from 'lucide-react';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { FormDialog } from '../common/form-dialog';
import { FormField, fieldA11y } from '../common/form-field';
import { useFormSubmit } from '../common/use-form-submit';
import { FIELD_TYPE_LABELS, wideningTargets } from './field-type-labels';

type FormInput = z.input<typeof ChangeFieldTypeSchema>;

/** Whitelisted widenings only (`TYPE_WIDENINGS`); the API rewrites stored values in a job. */
export function ChangeTypeDialog({
  field,
  onClose,
}: {
  field: FieldDefinition;
  onClose: () => void;
}) {
  const id = useId();
  const targets = wideningTargets(field.type);
  const change = useChangeFieldType();
  const form = useForm<FormInput, unknown, ChangeFieldType>({
    resolver: zodResolver(ChangeFieldTypeSchema),
    defaultValues: { toType: targets[0] },
  });
  const submit = useFormSubmit(form);
  const { errors } = form.formState;
  const toType = form.watch('toType');

  return (
    <FormDialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={`Change type of ${field.label}`}
      description={`Currently ${FIELD_TYPE_LABELS[field.type]}. Only widenings that need no interpretation of existing values are offered.`}
      formId={`${id}-form`}
      submitLabel="Change type"
      size="sm"
      busy={submit.busy}
      error={submit.error}
      omitPaths={submit.omitPaths}
      onSubmit={form.handleSubmit(async (values) => {
        const result = await submit.run(() => change.mutateAsync({ id: field.id, ...values }));
        if (result) {
          toast.success(`${field.label} is now ${FIELD_TYPE_LABELS[values.toType]}`);
          onClose();
        }
      })}
    >
      <FormField label="New type" htmlFor={`${id}-to`} required error={errors.toType?.message}>
        <Controller
          control={form.control}
          name="toType"
          render={({ field: f }) => (
            <Select value={f.value} onValueChange={(v) => f.onChange(v as FieldType)}>
              <SelectTrigger {...fieldA11y(`${id}-to`, { error: errors.toType })}>
                <SelectValue placeholder="Pick a type" />
              </SelectTrigger>
              <SelectContent>
                {targets.map((t) => (
                  <SelectItem key={t} value={t}>
                    {FIELD_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </FormField>
      <div
        role="note"
        className="flex gap-2 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 p-3 text-amber-900 dark:text-amber-300 text-sm"
      >
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="space-y-1">
          <p className="font-medium">This cannot be undone.</p>
          <p>
            Existing values are rewritten in place as{' '}
            {toType ? FIELD_TYPE_LABELS[toType] : 'the new type'}; the field&apos;s schema version
            increments and bound projects bump their configuration version. Index flags may be
            dropped when the new type is not indexable.
          </p>
        </div>
      </div>
    </FormDialog>
  );
}
